
import {  ApiSyncType, ApiSimpleComponent, ApiExternalEntityStatus, LastRunInfoServiceGetLastRunInfoTypeEnum, ApiClassificationItem, ApiCampaignType } from "@incendium/api";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  FormControlLabel,
} from "@mui/material";
import { ExpandLess, ExpandMore, Pause } from "@mui/icons-material";
import { useMetaUtmEntites } from "features/dataConnectors/hooks/useMetaUtmEntites";
import { buildEntityHierarchy } from "features/rezonate/utils/buildEntityHierarchy";
import { useExternalSyncs } from "Hooks/useExternalSyncs";
import { useLanders } from "Hooks/useLanders";
import { useComponents } from "Hooks/useComponents";
import { useNotification, useSelectedProject, usePolling, useClassifications } from "Hooks";
import { useCampaigns } from "Hooks/useCampaigns";
import Loading from "Components/Loading/Loading";
import { CenterPage } from "Components/CenterPage/CenterPage";
import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { Portal } from "@mui/material";
import produce from "immer";
import GlassCard from "Components/GlassCard/GlassCard";
import EntityTypeChip from "features/rezonate/components/EntityTypeChip";
import useProtectedRoutes from "Hooks/useProtectedRoutes";
import { RouteNames } from "Hooks/routeNames";
import { useHistory } from "react-router-dom";
import BatchCreateLanderDialog from "features/rezonate/components/BatchCreateLanderDialog";
import { batchGenerateComponentsFromAds, readComponent } from "features/campaigns/services/componentService";
import { usePollPendingComponents } from "features/campaigns";
import { friendlyDateTime } from "Helpers/dates";
import { listUtmEntities, refreshFacebookEntities } from "features/dataConnectors/services/utmEntities";
import { landersService, lastRunInfoService } from "Apis";
import LanderPreviewDialog from "features/lightningLanders/components/LanderPreviewDialog";
import SmartLinkUrlCard from "features/rezonate/components/SmartLinkUrlCard";
import { truncate } from "Helpers/truncate";
import { updateSmartLink } from "features/rezonate/services/smartLinkService";
import { updateSmartLinkEntities } from "features/rezonate/utils/updateSmartLinkEntities";
import { SmartLinkEntity, EntityLinkState } from "features/rezonate/components/SmartLinkCell";
import { ApiUTMEntityLevel } from "@incendium/api";
import { SmartLinkCell } from "features/rezonate/components/SmartLinkCell";
import { getInheritedLink } from "features/rezonate/utils/getInheritedLink";
import { EntityType } from "features/rezonate/types";

interface SelectedAds {
  [adId: number]: boolean;
}


function RezonateLandingPagesPage() {
  const { selectedProject } = useSelectedProject();
  const history = useHistory();
  const { entites, loading: entitiesLoading, setEntites, fetched: entitiesFetched, refetchEntites } = useMetaUtmEntites();
  const { externalSyncs, loading: externalSyncsLoading, fetched: externalSyncsFetched } = useExternalSyncs();
  const { landers, loading: landersLoading, refetch: refetchLanders, setLanders } = useLanders();
  const { components, loading: componentsLoading, setComponents } = useComponents();
  const { classifications } = useClassifications();
  const { campaigns, loading: campaignsLoading } = useCampaigns();

  const splitCampaigns = useMemo(
    () => campaigns.filter((c) => c.type === ApiCampaignType.CAMPAIGN_TYPE_SPLIT),
    [campaigns]
  );
  const { showErrorNotification, showSuccessNotification } = useNotification();
  const { pathByName } = useProtectedRoutes();
  const [selectedAds, setSelectedAds] = useState<SelectedAds>({});
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<number>>(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState<Set<number>>(new Set());
  const [batchCreateDialogOpen, setBatchCreateDialogOpen] = useState(false);
  const [pendingAdIds, setPendingAdIds] = useState<Set<number>>(new Set());
  const knownLandingPathIds = useRef<Set<number>>(new Set());
  const [previewComponent, setPreviewComponent] = useState<ApiSimpleComponent | null>(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [entityStates, setEntityStates] = useState<Record<string, EntityLinkState>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [refreshing, setRefreshing] = useState(false);
  const refreshStartTimestampRef = useRef<Date | null>(null);
  const [activeOnly, setActiveOnly] = useState(false);

  const { data: lastRunInfoResponse, loading: lastRunInfoLoading } = usePolling({
    pollFn: async () => {
      if (!selectedProject?.id) {
        return null;
      }
      return await lastRunInfoService.lastRunInfoServiceGetLastRunInfo({
        projectId: selectedProject.id as number,
        type: LastRunInfoServiceGetLastRunInfoTypeEnum.LAST_RUN_INFO_NAME_REFRESH_FACEBOOK_ENTITIES,
      });
    },
    interval: 5000,
    enabled: !!selectedProject?.id,
    immediate: true,
  });

  const lastRunInfo = lastRunInfoResponse?.lastRunInfo;

  useEffect(() => {
    if (refreshing && refreshStartTimestampRef.current && lastRunInfo?.updatedAt) {
      const newTimestamp = new Date(lastRunInfo.updatedAt);
      const startTimestamp = refreshStartTimestampRef.current;

      // If the new timestamp is at least 1 second after when we started the refresh, 
      const timeDiff = newTimestamp.getTime() - startTimestamp.getTime();
      if (timeDiff >= 1000) {
        setRefreshing(false);
        refreshStartTimestampRef.current = null;
        // Refetch entities since they may have changed after the refresh
        refetchEntites();
      }
    }
  }, [refreshing, lastRunInfo?.updatedAt, refetchEntites]);

  // Safety timeout: if refresh is still running after 10 minutes, stop it
  useEffect(() => {
    if (refreshing && refreshStartTimestampRef.current) {
      const timeout = setTimeout(() => {
        setRefreshing(false);
        refreshStartTimestampRef.current = null;
        showErrorNotification("Refresh is taking longer than expected. Please try again.");
      }, 10 * 60 * 1000); // 10 minutes

      return () => clearTimeout(timeout);
    }
  }, [refreshing, showErrorNotification]);

  const metaSync = useMemo(() => {
    return externalSyncs.find((es) => es.type === ApiSyncType.FACEBOOK_ADS);
  }, [externalSyncs]);

  const campaignGroups = useMemo(() => {
    const arr = buildEntityHierarchy(entites);
    if (!activeOnly) return arr;
    return arr
      .filter((cg) => cg.campaign.entityStatus === ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_ACTIVE)
      .map((cg) => ({
        campaign: cg.campaign,
        adGroups: cg.adGroups
          .filter((ag) => ag.adGroup.entityStatus === ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_ACTIVE)
          .map((ag) => ({
            adGroup: ag.adGroup,
            ads: ag.ads.filter((ad) => ad.entityStatus === ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_ACTIVE),
          }))
          .filter((ag) => ag.ads.length > 0),
      }))
      .filter((cg) => cg.adGroups.length > 0);
  }, [entites, activeOnly]);

  const findAdById = useCallback(
    (adId: number) => {
      for (const group of campaignGroups) {
        for (const ag of group.adGroups) {
          const ad = ag.ads.find((a) => a.id === adId);
          if (ad) return ad;
        }
      }
      return null;
    },
    [campaignGroups]
  );

  // Get all ad IDs for a campaign
  const getCampaignAdIds = useCallback(
    (campaignId: number): number[] => {
      const campaign = campaignGroups.find((cg) => cg.campaign.id === campaignId);
      if (!campaign) return [];
      const adIds: number[] = [];
      campaign.adGroups.forEach((ag) => {
        ag.ads.forEach((ad) => {
          adIds.push(ad.id);
        });
      });
      return adIds;
    },
    [campaignGroups]
  );

  // Get all ad IDs for an ad group
  const getAdGroupAdIds = useCallback(
    (campaignId: number, adGroupId: number): number[] => {
      const campaign = campaignGroups.find((cg) => cg.campaign.id === campaignId);
      if (!campaign) return [];
      const adGroup = campaign.adGroups.find((ag) => ag.adGroup.id === adGroupId);
      if (!adGroup) return [];
      return adGroup.ads.map((ad) => ad.id);
    },
    [campaignGroups]
  );

  // Check if all ads in a campaign are selected
  const isCampaignAllSelected = useCallback(
    (campaignId: number): boolean => {
      const adIds = getCampaignAdIds(campaignId);
      const selectableAdIds = adIds.filter((id) => {
        const ad = findAdById(id);
        return ad && !ad.currentLandingPathId;
      });
      if (selectableAdIds.length === 0) return false;
      return selectableAdIds.every((id) => selectedAds[id]);
    },
    [getCampaignAdIds, selectedAds, findAdById]
  );

  // Check if some ads in a campaign are selected
  const isCampaignSomeSelected = useCallback(
    (campaignId: number): boolean => {
      const adIds = getCampaignAdIds(campaignId);
      return adIds.some((id) => {
        const ad = findAdById(id);
        return ad && !ad.currentLandingPathId && selectedAds[id];
      });
    },
    [getCampaignAdIds, selectedAds, findAdById]
  );

  // Check if all ads in an ad group are selected
  const isAdGroupAllSelected = useCallback(
    (campaignId: number, adGroupId: number): boolean => {
      const adIds = getAdGroupAdIds(campaignId, adGroupId);
      const selectableAdIds = adIds.filter((id) => {
        const ad = findAdById(id);
        return ad && !ad.currentLandingPathId;
      });
      if (selectableAdIds.length === 0) return false;
      return selectableAdIds.every((id) => selectedAds[id]);
    },
    [getAdGroupAdIds, selectedAds, findAdById]
  );

  // Check if some ads in an ad group are selected
  const isAdGroupSomeSelected = useCallback(
    (campaignId: number, adGroupId: number): boolean => {
      const adIds = getAdGroupAdIds(campaignId, adGroupId);
      return adIds.some((id) => {
        const ad = findAdById(id);
        return ad && !ad.currentLandingPathId && selectedAds[id];
      });
    },
    [getAdGroupAdIds, selectedAds, findAdById]
  );

  // Toggle campaign selection (select all ads in campaign)
  const handleCampaignToggle = useCallback(
    (campaignId: number) => {
      const adIds = getCampaignAdIds(campaignId);
      const allSelected = isCampaignAllSelected(campaignId);
      const newSelected = { ...selectedAds };

      adIds.forEach((id) => {
        const ad = findAdById(id);
        if (ad && !ad.currentLandingPathId) {
          if (allSelected) {
            delete newSelected[id];
          } else {
            newSelected[id] = true;
          }
        }
      });

      setSelectedAds(newSelected);
    },
    [getCampaignAdIds, isCampaignAllSelected, selectedAds, findAdById]
  );

  // Toggle ad group selection (select all ads in ad group)
  const handleAdGroupToggle = useCallback(
    (campaignId: number, adGroupId: number) => {
      const adIds = getAdGroupAdIds(campaignId, adGroupId);
      const allSelected = isAdGroupAllSelected(campaignId, adGroupId);
      const newSelected = { ...selectedAds };

      adIds.forEach((id) => {
        const ad = findAdById(id);
        if (ad && !ad.currentLandingPathId) {
          if (allSelected) {
            delete newSelected[id];
          } else {
            newSelected[id] = true;
          }
        }
      });

      setSelectedAds(newSelected);
    },
    [getAdGroupAdIds, isAdGroupAllSelected, selectedAds, findAdById]
  );

  // Toggle individual ad selection
  const handleAdToggle = useCallback(
    (adId: number) => {
      const newSelected = { ...selectedAds };
      if (newSelected[adId]) {
        delete newSelected[adId];
      } else {
        newSelected[adId] = true;
      }
      setSelectedAds(newSelected);
    },
    [selectedAds, findAdById]
  );

  // Toggle campaign expansion
  const handleCampaignExpand = useCallback((campaignId: number) => {
    setExpandedCampaigns((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
      }
      return next;
    });
  }, []);

  // Toggle ad group expansion
  const handleAdGroupExpand = useCallback((adGroupId: number) => {
    setExpandedAdGroups((prev) => {
      const next = new Set(prev);
      if (next.has(adGroupId)) {
        next.delete(adGroupId);
      } else {
        next.add(adGroupId);
      }
      return next;
    });
  }, []);

  // Get selected ads count
  const selectedAdsCount = useMemo(() => {
    return Object.keys(selectedAds).filter((id) => selectedAds[Number(id)]).length;
  }, [selectedAds]);

  // Handle create lander
  const handleCreateLander = useCallback(() => {
    if (selectedAdsCount === 0) {
      showErrorNotification("Please select at least one ad");
      return;
    }

    setBatchCreateDialogOpen(true);
  }, [selectedAdsCount, showErrorNotification]);

  // Handle batch create confirmation
  const handleBatchCreateConfirm = useCallback(
    async (templateId: number, copyOnly: boolean) => {
      if (!selectedProject?.id) {
        showErrorNotification("No project selected");
        return;
      }

      const adIds = Object.keys(selectedAds)
        .filter((id) => selectedAds[Number(id)])
        .map((id) => Number(id));

      if (adIds.length === 0) {
        showErrorNotification("Please select at least one ad");
        return;
      }

      try {
        await batchGenerateComponentsFromAds(
          selectedProject.id,
          adIds,
          templateId,
          copyOnly
        );
        showSuccessNotification(
          `Landing page creation initiated for ${adIds.length} ad${adIds.length > 1 ? "s" : ""
          }`
        );
        // Track which ads we're waiting for components to appear for
        setPendingAdIds(new Set(adIds));
        // Clear selected ads after successful creation
        setSelectedAds({});
      } catch (error: any) {
        const errorMessage =
          error?.message || "Failed to create landing pages";
        showErrorNotification(errorMessage);
        throw error;
      }
    },
    [selectedProject?.id, selectedAds, showErrorNotification, showSuccessNotification]
  );


  const handleEditLander = useCallback((componentId: number) => {
    history.push(pathByName(RouteNames.LIGHTNING_LANDING_PAGE, {
      clientId: selectedProject?.clientId,
      projectId: selectedProject?.id,
      componentId: componentId,
    }));
  }, [pathByName, selectedProject?.clientId, selectedProject?.id, history]);

  const handleStateChange = useCallback((key: string, updates: Partial<EntityLinkState>) => {
    setEntityStates((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { linkType: "lander" as const, selectedLanderId: "", customUrl: "", selectedCampaignId: "", selectedClassificationItems: [] }), ...updates },
    }));
  }, []);

  // Initialize state from entities
  useEffect(() => {
    const newEntityStates: Record<string, EntityLinkState> = {};

    entites.forEach((entity) => {
      // Campaigns
      if (entity.externalCampaign?.id) {
        const key = `campaign-${entity.externalCampaign.id}`;
        if (!(key in newEntityStates)) {
          const landingPathId = entity.externalCampaign.landingPathId;
          const smartLinkUrl = entity.externalCampaign.smartLinkUrl;
          const splitCampaignId = entity.externalCampaign.splitCampaignId;

          newEntityStates[key] = {
            linkType: landingPathId != null ? "lander" : smartLinkUrl ? "url" : splitCampaignId ? "campaign" : "lander",
            selectedLanderId: landingPathId != null ? landingPathId : "",
            customUrl: smartLinkUrl || "",
            selectedCampaignId: splitCampaignId != null ? splitCampaignId : "",
            selectedClassificationItems: entity.externalCampaign?.classificationItems || entity.classificationItems || [],
          };
        }
      }

      // Ad Groups
      if (entity.externalAdgroup?.id && entity.externalCampaign?.id) {
        const key = `adGroup-${entity.externalAdgroup.id}`;
        if (!(key in newEntityStates)) {
          const landingPathId = entity.externalAdgroup.landingPathId;
          const smartLinkUrl = entity.externalAdgroup.smartLinkUrl;
          const splitCampaignId = entity.externalAdgroup.splitCampaignId;

          newEntityStates[key] = {
            linkType: landingPathId != null ? "lander" : smartLinkUrl ? "url" : splitCampaignId ? "campaign" : "lander",
            selectedLanderId: landingPathId != null ? landingPathId : "",
            customUrl: smartLinkUrl || "",
            selectedCampaignId: splitCampaignId != null ? splitCampaignId : "",
            selectedClassificationItems: entity.externalAdgroup?.classificationItems || entity.classificationItems || [],
          };
        }
      }

      // Ads
      if (entity.id) {
        const key = `ad-${entity.id}`;
        const landingPathId = entity.landingPathId;
        const rezonateUrl = entity.rezonateUrl;
        const splitCampaignId = entity.splitCampaignId;

        newEntityStates[key] = {
          linkType: landingPathId != null ? "lander" : rezonateUrl ? "url" : splitCampaignId ? "campaign" : "lander",
          selectedLanderId: landingPathId != null ? landingPathId : "",
          customUrl: rezonateUrl || "",
          selectedCampaignId: splitCampaignId != null ? splitCampaignId : "",
          selectedClassificationItems: entity.classificationItems || [],
        };
      }
    });

    // Reset state completely to avoid stale values
    setEntityStates(newEntityStates);
  }, [entites]);

  // Handle save smart link
  const handleSaveSmartLink = useCallback(
    async (
      entity: SmartLinkEntity,
      linkType: "lander" | "url" | "campaign",
      value: number | string | null,
      clearValue?: string | null,
      classificationItemIds?: number[]
    ) => {
      if (!selectedProject?.id || !metaSync) {
        showErrorNotification("Project or sync not found");
        return;
      }

      const key = `${entity.type}-${entity.id}`;
      setSaving((prev) => ({ ...prev, [key]: true }));

      try {
        const landingPathId =
          linkType === "lander" ? (value as number | null) :
          linkType === "campaign" ? null :
          (clearValue === null ? null : undefined);

        const rezonateUrl =
          linkType === "url" ? (value as string) :
          linkType === "campaign" ? "" :
          (clearValue === "" ? "" : undefined);

        const splitCampaignId =
          linkType === "campaign" ? (value as number | null) :
          (linkType === "lander" || linkType === "url") ? null :
          undefined;

        await updateSmartLink(
          selectedProject.id as number,
          entity.level,
          entity.id,
          landingPathId,
          rezonateUrl,
          classificationItemIds,
          splitCampaignId
        );

        showSuccessNotification(
          `${entity.type === "campaign" ? "Campaign" : entity.type === "adGroup" ? "Ad Group" : "Ad"} smart link updated successfully`
        );

        const currentState = entityStates[key] || {
          linkType: "lander" as const,
          selectedLanderId: "",
          customUrl: "",
          selectedCampaignId: "",
          selectedClassificationItems: [],
        };

        let updatedClassificationItems = currentState.selectedClassificationItems || [];
        if (classificationItemIds !== undefined) {
          const allClassificationItems: ApiClassificationItem[] = [];
          classifications.forEach((classification) => {
            if (classification.classificationItems) {
              allClassificationItems.push(...classification.classificationItems);
            }
          });
          updatedClassificationItems = classificationItemIds
            .map((id) => allClassificationItems.find((item) => item.id === id))
            .filter((item): item is ApiClassificationItem => item !== undefined);
        }

        setEntityStates((prev) => ({
          ...prev,
          [key]: {
            linkType,
            selectedLanderId: linkType === "lander" ? (value as number | "") : "",
            customUrl: linkType === "url" ? (value as string) : "",
            selectedCampaignId: linkType === "campaign" ? (value as number | "") : "",
            selectedClassificationItems: updatedClassificationItems,
          },
        }));

        // Update entities in shared state to reflect the change
        setEntites((prev) =>
          updateSmartLinkEntities(
            prev,
            entity.level,
            entity.id,
            linkType === "lander" ? (value !== null ? (value as number) : undefined) : undefined,
            linkType === "url" ? (value as string) : (clearValue === "" ? "" : undefined),
            updatedClassificationItems,
            splitCampaignId
          )
        );
      } catch (error) {
        showErrorNotification("Failed to update smart link");
      } finally {
        setSaving((prev) => ({ ...prev, [key]: false }));
      }
    },
    [selectedProject, metaSync, showSuccessNotification, showErrorNotification, setEntites, classifications, entityStates]
  );
  const refreshEntites = useCallback(async () => {
    if (!selectedProject?.id || !metaSync?.id || refreshing) {
      return;
    }

    refreshStartTimestampRef.current = new Date();
    setRefreshing(true);

    try {
      await refreshFacebookEntities(selectedProject.id, metaSync.id);
    } catch (error) {
      setRefreshing(false);
      refreshStartTimestampRef.current = null;
      showErrorNotification("Failed to refresh ads list");
    }
  }, [selectedProject, metaSync, refreshing, showErrorNotification]);

  useEffect(() => {
    if (pendingAdIds.size === 0 || !selectedProject?.id) {
      return;
    }

    const metaSync = externalSyncs.find((es) => es.type === ApiSyncType.FACEBOOK_ADS);
    if (!metaSync) {
      return;
    }

    let attemptCount = 0;
    const maxAttempts = 12; // 1 minute total (12 * 5 seconds)

    const checkInterval = setInterval(async () => {
      attemptCount++;

      if (attemptCount > maxAttempts) {
        setPendingAdIds(new Set());
        return;
      }

      try {
        // Poll UTM entities to check for new landing path IDs
        const updatedEntities = await listUtmEntities(selectedProject.id, metaSync.id);

        // Find new landing path IDs that we haven't seen before
        const newLandingPathIds = new Set<number>();
        const adsWithLanders = new Set<number>();

        for (const adId of pendingAdIds) {
          const entity = updatedEntities.find((e) => e.id === adId);
          const landingPathId = entity?.landingPathId;
          if (landingPathId) {
            // Check if this is a new landing path we haven't seen
            if (!knownLandingPathIds.current.has(landingPathId)) {
              newLandingPathIds.add(landingPathId);
              knownLandingPathIds.current.add(landingPathId);
            }

            adsWithLanders.add(adId);
          }
        }

        // Fetch new landing paths and add them to state
        if (newLandingPathIds.size > 0) {
          for (const landingPathId of Array.from(newLandingPathIds)) {
            if (typeof landingPathId !== 'number' || !selectedProject.id) continue;
            try {
              const lander = await landersService.landersServiceReadLander({
                projectId: selectedProject.id as number,
                landerId: landingPathId,
              });

              // Add lander to state
              setLanders((currentLanders) =>
                produce(currentLanders, (draft) => {
                  // Check if lander already exists
                  const exists = draft.some((l) => l.id === lander.id);
                  if (!exists) {
                    draft.push(lander);
                  }
                })
              );

              // If lander has a componentId, fetch the component if we don't have it
              if (lander.componentId) {
                const hasComponent = components.some((c) => c.id === lander.componentId);
                if (!hasComponent) {
                  try {
                    const componentRes = await readComponent(
                      selectedProject.id,
                      lander.componentId
                    );
                    if (componentRes.component) {
                      setComponents((currentComponents) =>
                        produce(currentComponents, (draft) => {
                          const exists = draft.some((c) => c.id === componentRes.component?.id);
                          if (!exists) {
                            draft.push(componentRes.component!);
                          }
                        })
                      );
                    }
                  } catch (error) {
                    console.error("Error fetching component:", error);
                  }
                }
              }
            } catch (error) {
              console.error("Error fetching lander:", error);
            }
          }
        }

        // Update entities for pending ads
        setEntites((currentEntities) =>
          produce(currentEntities, (draft) => {
            for (const adId of pendingAdIds) {
              const updatedEntity = updatedEntities.find((e) => e.id === adId);
              if (updatedEntity) {
                const idx = draft.findIndex((e) => e.id === adId);
                if (idx >= 0) {
                  draft[idx] = updatedEntity;
                }
              }
            }
          })
        );

        // Remove ads that now have landers from pending set
        if (adsWithLanders.size > 0) {
          setPendingAdIds((prev) => {
            const next = new Set(prev);
            adsWithLanders.forEach((adId) => next.delete(adId));
            return next;
          });
        }

        // If all pending ads have landers, stop checking
        if (adsWithLanders.size === pendingAdIds.size) {
          setPendingAdIds(new Set());
        }
      } catch (error) {
        console.error("Error polling for ad updates:", error);
      }
    }, 5000); // Check every 5 seconds

    return () => clearInterval(checkInterval);
  }, [pendingAdIds, selectedProject?.id, setEntites, setLanders, setComponents, components, externalSyncs]);

  // Poll for components with pending AI prompts
  usePollPendingComponents({
    projectId: selectedProject?.id,
    components: components,
    setComponents: (updater) => {
      setComponents(updater);
      // Also refetch landers to get updated landing path info when component status changes
      refetchLanders();
    },
  });

  const handleConnectFacebookAdsAccount = useCallback(() => {
    history.push(pathByName(RouteNames.DATA_CONNECTIONS, { clientId: selectedProject?.clientId, projectId: selectedProject?.id }));
  }, [history, selectedProject?.clientId, selectedProject?.id, pathByName]);

  if (entitiesLoading || landersLoading || componentsLoading || externalSyncsLoading || campaignsLoading || !entitiesFetched) {
    return <Loading />;
  }

  if (!metaSync && externalSyncsFetched) {
    return (
      <CenterPage calcHeight>
        <Typography variant="h6" color="text.secondary" mb={2}>
          Connect a Facebook Ads account to manage landing pages.
        </Typography>
        <Button variant="contained" color="primary" onClick={handleConnectFacebookAdsAccount}>
          Connect Facebook Ads Account
        </Button>
      </CenterPage>
    );
  }

  return (
    <Box>
      <Portal container={() => document.getElementById("pageAction")}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateLander}
          disabled={selectedAdsCount === 0}
        >
          Create Landing Page ({selectedAdsCount})
        </Button>
      </Portal>

      <Box mb={2} display="flex">
        <SmartLinkUrlCard />
      </Box>
      <>
        <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h5" component="h2">
              Facebook Ads
            </Typography>
            <FormControlLabel
                control={<Checkbox checked={activeOnly} onChange={() => setActiveOnly(!activeOnly)} />}
                label="Show Active Only"
              />
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center">
          
            {lastRunInfoLoading || refreshing ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : lastRunInfo?.updatedAt ? (
              <Typography variant="body2" color="text.secondary">
                Last refreshed: {friendlyDateTime(lastRunInfo.updatedAt)}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Last refreshed: Never
              </Typography>
            )}
            <Button
              variant="outlined"
              onClick={refreshEntites}
              disabled={refreshing}
            >
              Refresh ads list
            </Button>
          </Stack>
        </Box>
        {campaignGroups.length === 0 && entitiesFetched ? (
          <CenterPage calcHeight>
            <Typography variant="h6" color="text.secondary">
              No {activeOnly ? "active" : ""} campaigns, ad sets, or ads found.
            </Typography>
          </CenterPage>
        ) : (
          <GlassCard boxProps={{ p: 0, overflow: "hidden" }}>
            <Table size="small" sx={{ transform: "translateY(1px)" }}>
              <colgroup>
                <col style={{ width: 50 }} />
                <col style={{ width: "auto", minWidth: 200 }} />
                <col style={{ width: "50%" }} />
              </colgroup>


              <TableBody>
                {campaignGroups.map(({ campaign, adGroups }) => {
                  const campaignExpanded = expandedCampaigns.has(campaign.id);
                  const campaignAllSelected = isCampaignAllSelected(campaign.id);
                  const campaignSomeSelected = isCampaignSomeSelected(campaign.id);

                  return (
                    <>
                      <TableRow key={`campaign-${campaign.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={campaignAllSelected}
                            indeterminate={campaignSomeSelected && !campaignAllSelected}
                            onChange={() => handleCampaignToggle(campaign.id)}
                          />
                        </TableCell>
                        <TableCell sx={{ width: "auto", minWidth: 200 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <IconButton
                              size="small"
                              onClick={() => handleCampaignExpand(campaign.id)}
                            >
                              {campaignExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                            <EntityTypeChip type={EntityType.CAMPAIGN} />
                            <Typography variant="subtitle1" fontWeight="bold">
                              {truncate(campaign.name || "", 100)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ width: "50%" }}>
                          {(() => {
                            const campaignKey = `campaign-${campaign.id}`;
                            const state = entityStates[campaignKey] || {
                              linkType: "lander" as const,
                              selectedLanderId: "",
                              customUrl: "",
                              selectedClassificationItems: [],
                            };
                            const campaignEntity: SmartLinkEntity = {
                              id: campaign.id,
                              name: campaign.name || "",
                              type: EntityType.CAMPAIGN,
                              level: ApiUTMEntityLevel.CAMPAIGN,
                              entityStatus: campaign.entityStatus,
                            };

                            return (
                              <SmartLinkCell
                                entity={campaignEntity}
                                landingPathId={campaign.currentLandingPathId}
                                smartLinkUrl={campaign.currentSmartLinkUrl}
                                splitCampaignId={campaign.currentSplitCampaignId}
                                state={state}
                                isSaving={saving[campaignKey] || false}
                                landers={landers}
                                campaigns={splitCampaigns}
                                onStateChange={handleStateChange}
                                onSave={handleSaveSmartLink}
                                onEditLander={handleEditLander}
                                onPreviewComponent={setPreviewComponent}
                                showStatus={true}
                              />
                            );
                          })()}
                        </TableCell>

                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={3} sx={{ p: 0, border: 0 }}>
                          <Collapse in={campaignExpanded} timeout="auto" unmountOnExit>
                            <Box>
                              <Table size="small">
                                <colgroup>
                                  <col style={{ width: 50 }} />
                                  <col style={{ width: "auto", minWidth: 200 }} />
                                  <col style={{ width: "50%" }} />
                                </colgroup>
                                <TableBody>
                                  {adGroups.map(({ adGroup, ads }) => {
                                    const adGroupExpanded = expandedAdGroups.has(adGroup.id);
                                    const adGroupAllSelected = isAdGroupAllSelected(
                                      campaign.id,
                                      adGroup.id
                                    );
                                    const adGroupSomeSelected = isAdGroupSomeSelected(
                                      campaign.id,
                                      adGroup.id
                                    );

                                    return (
                                      <>
                                        <TableRow key={`adgroup-${adGroup.id}`}>
                                          <TableCell sx={{ width: 50 }}>
                                            <Checkbox
                                              checked={adGroupAllSelected}
                                              indeterminate={
                                                adGroupSomeSelected && !adGroupAllSelected
                                              }
                                              onChange={() =>
                                                handleAdGroupToggle(campaign.id, adGroup.id)
                                              }
                                            />
                                          </TableCell>
                                          <TableCell sx={{ width: "auto", minWidth: 200 }}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                              <IconButton
                                                size="small"
                                                onClick={() => handleAdGroupExpand(adGroup.id)}
                                              >
                                                {adGroupExpanded ? <ExpandLess /> : <ExpandMore />}
                                              </IconButton>
                                              <EntityTypeChip type={EntityType.AD_GROUP} />
                                              <Typography variant="body1" fontWeight="medium">
                                                {truncate(adGroup.name || "", 100)}
                                              </Typography>
                                            </Stack>
                                          </TableCell>
                                          <TableCell sx={{ width: "50%" }}>
                                            {(() => {
                                              const adGroupKey = `adGroup-${adGroup.id}`;
                                              const inheritedLink = getInheritedLink(adGroup, undefined, campaign);
                                              const state = entityStates[adGroupKey] || {
                                                linkType: "lander" as const,
                                                selectedLanderId: "",
                                                customUrl: "",
                                                selectedClassificationItems: [],
                                              };
                                              const adGroupEntity: SmartLinkEntity = {
                                                id: adGroup.id,
                                                name: adGroup.name || "",
                                                type: "adGroup",
                                                level: ApiUTMEntityLevel.AD_GROUP,
                                                campaignName: adGroup.campaignName,
                                                entityStatus: adGroup.entityStatus,
                                              };

                                              return (
                                                <SmartLinkCell
                                                  entity={adGroupEntity}
                                                  landingPathId={inheritedLink.landingPathId}
                                                  smartLinkUrl={inheritedLink.smartLinkUrl}
                                                  splitCampaignId={inheritedLink.splitCampaignId}
                                                  state={state}
                                                  isSaving={saving[adGroupKey] || false}
                                                  landers={landers}
                                                  campaigns={splitCampaigns}
                                                  onStateChange={handleStateChange}
                                                  onSave={handleSaveSmartLink}
                                                  onEditLander={handleEditLander}
                                                  onPreviewComponent={setPreviewComponent}
                                                  inheritedFrom={inheritedLink.inheritedFrom}
                                                  showStatus={true}
                                                />
                                              );
                                            })()}
                                          </TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell colSpan={3} sx={{ p: 0, border: 0 }}>
                                            <Collapse in={adGroupExpanded} timeout="auto" unmountOnExit>
                                              <Box>
                                                <Table size="small">
                                                  <colgroup>
                                                    <col style={{ width: 50 }} />
                                                    <col style={{ width: "auto", minWidth: 200 }} />
                                                    <col style={{ width: "50%" }} />
                                                  </colgroup>
                                                  <TableBody>
                                                    {ads.map((ad, idx) => {
                                                      const inheritedLink = getInheritedLink(ad, adGroup, campaign);
                                                      const hasDirectLink = inheritedLink.inheritedFrom === "direct";
                                                      const isPending = pendingAdIds.has(ad.id);
                                                      const adKey = `ad-${ad.id}`;
                                                      const state = entityStates[adKey] || {
                                                        linkType: "lander" as const,
                                                        selectedLanderId: "",
                                                        customUrl: "",
                                                        selectedClassificationItems: [],
                                                      };
                                                      const adEntity: SmartLinkEntity = {
                                                        id: ad.id,
                                                        name: ad.name || "",
                                                        type: "ad",
                                                        level: ApiUTMEntityLevel.AD,
                                                        campaignName: ad.campaignName,
                                                        adGroupName: ad.adGroupName,
                                                        entityStatus: ad.entityStatus,
                                                      };

                                                      return (
                                                        <TableRow key={`ad-${ad.id}`} >
                                                          <TableCell sx={{ width: 50 }}>
                                                            <Checkbox
                                                              checked={!!selectedAds[ad.id]}
                                                              onChange={() => handleAdToggle(ad.id)}
                                                              disabled={hasDirectLink || isPending}
                                                            />
                                                          </TableCell>
                                                          <TableCell sx={{ width: "auto", minWidth: 200 }}>
                                                            <Typography variant="body1" fontWeight="medium">
                                                              {ad.entityStatus && ad.entityStatus === ApiExternalEntityStatus.EXTERNAL_ENTITY_STATUS_PAUSED && <Chip icon={<Pause />} label="Paused" variant="outlined" size="small" color="warning" />} {truncate(ad.name || "", 100)}
                                                            </Typography>
                                                          </TableCell>
                                                          <TableCell sx={{ width: "50%" }}>
                                                            {isPending ? (
                                                              <Stack direction="column" spacing={0.5}>
                                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                                                                  Creating landing page...
                                                                </Typography>
                                                              </Stack>
                                                            ) : (() => {
                                                              const inheritedLink = getInheritedLink(ad, adGroup, campaign);

                                                              return (
                                                                <SmartLinkCell
                                                                  entity={adEntity}
                                                                  landingPathId={inheritedLink.landingPathId}
                                                                  smartLinkUrl={inheritedLink.smartLinkUrl}
                                                                  splitCampaignId={inheritedLink.splitCampaignId}
                                                                  state={state}
                                                                  isSaving={saving[adKey] || false}
                                                                  landers={landers}
                                                                  campaigns={splitCampaigns}
                                                                  onStateChange={handleStateChange}
                                                                  onSave={handleSaveSmartLink}
                                                                  onEditLander={handleEditLander}
                                                                  onPreviewComponent={setPreviewComponent}
                                                                  inheritedFrom={inheritedLink.inheritedFrom}
                                                                  isPending={isPending}
                                                                  showStatus={true}
                                                                />
                                                              );
                                                            })()}
                                                          </TableCell>
                                                        </TableRow>
                                                      );
                                                    })}
                                                  </TableBody>
                                                </Table>
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      </>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </GlassCard>
        )
      }
      </>
      
      <BatchCreateLanderDialog
        open={batchCreateDialogOpen}
        onClose={() => setBatchCreateDialogOpen(false)}
        onConfirm={handleBatchCreateConfirm}
        selectedAdsCount={selectedAdsCount}
      />

      {previewComponent && (
        <LanderPreviewDialog
          open={!!previewComponent}
          onClose={() => setPreviewComponent(null)}
          component={previewComponent}
          title={`${previewComponent.name} - Preview`}
          fullScreen={fullScreen}
        />
      )}
    </Box >
  );
}

export default RezonateLandingPagesPage;
